import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { Project, ProjectConfig, Station, CalculationResult } from './types';
import { MatrixType } from './lib/url/matrixMapping';
import { ProjectStore } from './lib/storage/projects';
import { MetadataStore } from './lib/storage/metadata';
import { NetworkEditor } from './components/network/Editor';
import { ConfigForm } from './components/config/ConfigForm';
import { MatrixTable } from './components/matrices/MatrixTable';
import { SummaryView } from './components/summary/SummaryView';
import { ExportView } from './components/export/ExportView';
import { Header } from './components/Header';
import { parseGraphML } from './lib/import/graphml';
import { getDefaultCurrency, getCountryCode } from './utils/locale';
import { DEFAULT_CONFIG } from './types';
import { ExampleProjectLoader } from './lib/import/examples';
import { URLManager } from './lib/url/URLManager';
import { matrixToView, viewToMatrix, getDefaultMatrixType } from './lib/url/matrixMapping';
import { MatrixSelectionManager } from './lib/matrix/MatrixSelectionManager';
import { MatrixSelector } from './components/matrices/MatrixSelector';
import { shouldSkipDuplication, promptSkipDuplication } from './lib/storage/exampleSkip';

const EXAMPLE_PROJECT_COUNTER_KEY = 'railmapper_example_counter';
const NEW_PROJECT_COUNTER_KEY = 'railmapper_new_counter';

async function loadExampleProject(store: ProjectStore): Promise<Project | null> {
  try {
    await ExampleProjectLoader.loadAllExamples(store);
    const { userProjects, exampleProjects } = await store.getActiveProjects();
    const exampleProject = exampleProjects[0];
    if (exampleProject) {
      return await store.getProject(exampleProject.id);
    }
    return null;
  } catch (error) {
    console.error('Failed to load example project:', error);
    return null;
  }
}

type ViewType = 'network' | 'config' | 'results' | 'summary' | 'export';

function ProjectView() {
  const { identifier, page, view } = useParams<{ identifier?: string; page?: string; view?: string }>();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<string>(() => {
    return page || localStorage.getItem('railmapper_current_view') || 'network';
  });
  const [currentSubView, setCurrentSubView] = useState<string | null>(() => {
    const savedSubView = localStorage.getItem('railmapper_current_subview');
    return view || savedSubView || null;
  });
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [userProjects, setUserProjects] = useState<{ id: string; name: string; lastEdited: number }[]>([]);
  const [exampleProjects, setExampleProjects] = useState<{ id: string; name: string; lastEdited: number }[]>([]);
  const [matrices, setMatrices] = useState<CalculationResult | null>(null);
  const [selectedMatrix, setSelectedMatrix] = useState<MatrixType>(() => {
    return MatrixSelectionManager.getInstance().getCurrentMatrix();
  });
  const [cyInstance, setCyInstance] = useState<cytoscape.Core | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const downloadFnRef = useRef<() => void>(() => {});
  const projectStore = useRef<ProjectStore>();
  const calculationWorker = useRef<Worker>();
  const urlManager = useRef(URLManager.getInstance());
  const matrixManager = useRef(MatrixSelectionManager.getInstance());

  // Initialize storage and load projects
  useEffect(() => {
    let mounted = true;

    const initStorage = async () => {
      try {
        setIsInitializing(true);
        
        const store = new ProjectStore();
        await store.init();
        
        if (!mounted) return;
        
        projectStore.current = store;
        
        // Check for new/modified examples first
        await ExampleProjectLoader.loadAllExamples(store);
        
        // Get all projects (including any new examples)
        const { userProjects: userList, exampleProjects: exampleList } = await store.getActiveProjects();
        setUserProjects(userList);
        setExampleProjects(exampleList);

        // If we have an identifier in the URL, try to load that project
        if (identifier) {
          const project = await store.loadProjectByIdentifier(identifier);
          if (project && !project.isDeleted) {
            setCurrentProject(project);
          } else {
            // Project not found or deleted, redirect to most recent
            const mostRecent = userList[0] || exampleList[0];
            if (mostRecent) {
              const project = await store.getProject(mostRecent.id);
              if (project && !project.isDeleted) {
                switchToProject(project);
              }
            }
          }
        } else {
          // No identifier in URL, load most recent project
          const mostRecent = userList[0] || exampleList[0];
          if (mostRecent) {
            const project = await store.getProject(mostRecent.id);
            if (project && !project.isDeleted) {
              switchToProject(project);
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize storage:', error);
        if (mounted) {
          setIsInitializing(false);
          setCurrentProject(null);
        }
      } finally {
        if (mounted) {
          setIsInitializing(false);
        }
      }
    };

    // Initialize calculation worker
    calculationWorker.current = new Worker(
      new URL('./lib/calculations/worker.ts', import.meta.url),
      { type: 'module' }
    );

    calculationWorker.current.onmessage = (e) => {
      if (mounted) {
        setMatrices(e.data);
      }
    };

    initStorage();

    return () => {
      mounted = false;
      calculationWorker.current?.terminate();
    };
  }, [identifier]);

  // Handle URL changes
  useEffect(() => {
    const unsubscribe = urlManager.current.handleURLChange((newIdentifier, newPage, newView) => {
      if (newPage && newPage !== currentView) {
        setCurrentView(newPage);
        localStorage.setItem('railmapper_current_view', newPage);
      }
      if (newView !== undefined && newView !== currentSubView) {
        setCurrentSubView(newView);
        localStorage.setItem('railmapper_current_subview', newView || '');
        // If we're on the results page, update the selected matrix type
        if (newPage === 'results' && newView) {
          matrixManager.current.updateFromView(newView);
        }
      }
    });

    return () => unsubscribe();
  }, [currentView, currentSubView]);

  // Handle initial view state and URL updates
  useEffect(() => {
    if (currentProject) {
      // Only set default views when first loading a page, not when switching
      const isFirstLoad = !window.location.pathname.includes(currentProject.identifier);
      
      // If we have a view in the URL, use it
      if (view) {
        setCurrentSubView(view);
        localStorage.setItem('railmapper_current_subview', view);
        // If we're on the results page, update the selected matrix type
        if (currentView === 'results') {
          // Only sync with URL if we're not switching projects and the view matches the current matrix
          const currentMatrix = matrixManager.current.getCurrentMatrix();
          const matrixView = matrixManager.current.getViewFromMatrix(currentMatrix);
          if (!isFirstLoad && view === matrixView) {
            matrixManager.current.syncWithURL(view);
          }
        }
      } else if (isFirstLoad) {
        // Only set default views on first load
        if (currentView === 'results') {
          matrixManager.current.updateURL();
        } else if (currentView === 'network') {
          const defaultView = 'editor';
          setCurrentSubView(defaultView);
          localStorage.setItem('railmapper_current_subview', defaultView);
          urlManager.current.updateURL(currentProject.identifier, currentView, defaultView, 'auto', 'default_view');
        } else {
          setCurrentSubView(null);
          localStorage.removeItem('railmapper_current_subview');
        }
      }
    }
  }, [currentProject, view, currentView]);

  // Add new effect to sync matrix selection with manager
  useEffect(() => {
    const handleMatrixChange = () => {
      const newMatrix = matrixManager.current.getCurrentMatrix();
      setSelectedMatrix(newMatrix);
    };

    // Initial sync
    handleMatrixChange();

    // Subscribe to storage changes for cross-tab sync
    window.addEventListener('storage', handleMatrixChange);
    return () => window.removeEventListener('storage', handleMatrixChange);
  }, []);

  // Validate and handle view changes
  useEffect(() => {
    if (currentProject && view && !urlManager.current.validateView(currentView, view)) {
      // Only redirect for network and results pages
      if (currentView === 'network' || currentView === 'results') {
        const defaultView = urlManager.current.getDefaultView(currentView);
        if (defaultView) {
          navigate(`/project/${currentProject.identifier}/${currentView}/${defaultView}`);
        }
      } else {
        // For other pages like config, just clear the view
        navigate(`/project/${currentProject.identifier}/${currentView}`);
      }
    }
  }, [view, currentView, currentProject, navigate]);

  // Save current view whenever it changes
  useEffect(() => {
    localStorage.setItem('railmapper_current_view', currentView);
  }, [currentView]);

  // Debug logging for matrices and selectedMatrix
  useEffect(() => {
    if (matrices && currentView === 'results') {
      // Only log if there's an issue with the matrix data
      if (!('cities' in matrices) || !Array.isArray(matrices.cities)) {
        console.warn('Matrix data structure issue:', {
          hasCities: 'cities' in matrices,
          isArray: 'cities' in matrices && Array.isArray(matrices.cities)
        });
      }
    }
  }, [selectedMatrix, matrices, currentView]);

  // Trigger calculations when project changes
  useEffect(() => {
    if (currentProject && calculationWorker.current) {
      console.debug('Matrix calculations started for:', currentProject.name);
      calculationWorker.current.postMessage({
        network: currentProject.network,
        config: currentProject.config
      });
    }
  }, [currentProject]);

  // Update project with calculated matrices
  useEffect(() => {
    if (matrices && currentProject) {
      // Only update if matrices have actually changed
      if (JSON.stringify(matrices) !== JSON.stringify(currentProject.matrices)) {
        handleProjectChange({
          ...currentProject,
          matrices
        }, false);  // Don't update timestamp for matrix changes
      }
    }
  }, [matrices]);

  const switchToProject = async (project: Project) => {
    // Always get a fresh copy of the project from storage
    if (projectStore.current) {
      const freshProject = await projectStore.current.getProject(project.id);
      if (freshProject) {
        // Clear existing Cytoscape instance
        if (cyInstance) {
          try {
            cyInstance.destroy();
          } catch (error) {
            console.warn('Error destroying Cytoscape instance:', error);
          }
          setCyInstance(null);
        }
        
        // Update project state
        setCurrentProject(freshProject);
        
        // Get the current matrix and view
        const currentMatrix = matrixManager.current.getCurrentMatrix();
        const view = matrixManager.current.getViewFromMatrix(currentMatrix);
        
        // Update the matrix selection first
        await matrixManager.current.handleMenuSelection(currentMatrix);
        
        // Then update the URL
        urlManager.current.updateURL(freshProject.identifier, currentView, view, 'auto', 'project_switch');
        
        if (calculationWorker.current) {
          calculationWorker.current.postMessage({
            network: freshProject.network,
            config: freshProject.config
          });
        }
      }
    }
  };

  const createNewProject = async (store?: ProjectStore) => {
    // Get and increment the counter
    const counter = parseInt(localStorage.getItem(NEW_PROJECT_COUNTER_KEY) || '0', 10) + 1;
    localStorage.setItem(NEW_PROJECT_COUNTER_KEY, counter.toString());
    
    // Only add version number if this is not the first new project
    const projectName = counter === 1 
      ? 'New Project'
      : `New Project v${counter}`;
    
    // Get locale and currency information
    const locale = navigator.language;
    const countryCode = getCountryCode();
    const currency = getDefaultCurrency();
    
    const newProject: Project = {
      id: crypto.randomUUID(),
      identifier: '', // Will be set by ensureProjectIdentifier
      name: projectName,
      lastEdited: Date.now(),
      type: 'user',  // New projects are always user projects
      config: {
        ...DEFAULT_CONFIG,
        bond: {
          ...DEFAULT_CONFIG.bond,
          currency // Get fresh currency value at creation time
        }
      },
      network: {
        stations: [],
        routes: []
      }
    };
    
    try {
      const currentStore = store || projectStore.current;
      if (currentStore) {
        // Ensure project has a unique identifier
        const projectWithIdentifier = await currentStore.ensureProjectIdentifier(newProject);
        await currentStore.saveProject(projectWithIdentifier);
        const { userProjects, exampleProjects } = await currentStore.getActiveProjects();
        setUserProjects(userProjects);
        setExampleProjects(exampleProjects);
        switchToProject(projectWithIdentifier);
        setCurrentView('network');
        // Update URL with the new project identifier
        urlManager.current.updateURL(projectWithIdentifier.identifier, 'network', null, 'auto', 'new_project_creation');
      }
    } catch (error) {
      console.error('Failed to create new project:', error);
      setCurrentProject(null);
    }
  };

  const handleProjectChange = async (projectOrUpdater: Project | ((currentProject: Project) => Project), updateTimestamp: boolean = true) => {
    try {
      if (projectStore.current) {
        const updatedProject = typeof projectOrUpdater === 'function'
          ? projectOrUpdater(currentProject!)
          : projectOrUpdater;

        // Check if this is an example project being edited
        if (updatedProject.type === 'example' && updateTimestamp) {
          // Check if we should skip duplication
          if (shouldSkipDuplication()) {
            // Just save the changes without duplication
            await projectStore.current.saveProject(updatedProject, true);
            setCurrentProject(updatedProject);
            return;
          }

          // If not skipping, prompt the user
          if (promptSkipDuplication()) {
            // User chose to skip duplication
            await projectStore.current.saveProject(updatedProject, true);
            setCurrentProject(updatedProject);
            return;
          }

          // User chose not to skip, proceed with normal duplication
          const userProject = await projectStore.current.handleExampleEdit(updatedProject);
          
          // Get fresh project list first to ensure correct sorting
          const { userProjects: userList, exampleProjects: exampleList } = await projectStore.current.getActiveProjects();
          setUserProjects(userList);
          setExampleProjects(exampleList);
          
          // Switch to the user's copy after updating the list
          setCurrentProject(userProject);
          
          // Update URL with the new project identifier
          urlManager.current.updateURL(userProject.identifier, currentView, view || null, 'auto', 'example_edit');
          
          return;
        }

        // Update current project immediately
        setCurrentProject(updatedProject);
        
        // Save to database first to ensure metadata is updated
        await projectStore.current.saveProject(updatedProject, updateTimestamp);
        
        // Get fresh project list to ensure correct sorting
        const { userProjects: userList, exampleProjects: exampleList } = await projectStore.current.getActiveProjects();
        setUserProjects(userList);
        setExampleProjects(exampleList);
      }
    } catch (error) {
      console.error('Failed to update project:', error);
      // TODO: Show error notification to user
    }
  };

  const handleProjectSelect = async (id: string) => {
    if (id === 'new') {
      createNewProject();
      return;
    }
    
    if (id === 'example' && projectStore.current) {
      const exampleProject = await loadExampleProject(projectStore.current);
      if (exampleProject) {
        await switchToProject(exampleProject);
        const { userProjects, exampleProjects } = await projectStore.current.getActiveProjects();
        setUserProjects(userProjects);
        setExampleProjects(exampleProjects);
      }
      return;
    }
    
    if (projectStore.current) {
      const project = await projectStore.current.getProject(id);
      
      if (project && !project.isDeleted) {
        await switchToProject(project);
      }
    }
  };

  const handleDeleteProject = async () => {
    if (!currentProject || !projectStore.current) return;

    try {
      // Immediately hide from UI
      setUserProjects(prev => prev.filter(p => p.id !== currentProject.id));
      setCurrentProject(null);

      // Perform soft delete
      await projectStore.current.softDeleteProject(currentProject.id);

      // Get updated project list and switch to next project or create new one
      const { userProjects: userList, exampleProjects: exampleList } = await projectStore.current.getActiveProjects();
      setUserProjects(userList);
      setExampleProjects(exampleList);

      if (userList.length > 0) {
        const nextProject = await projectStore.current.getProject(userList[0].id);
        if (nextProject && !nextProject.isDeleted) {
          switchToProject(nextProject);
        } else {
          await createNewProject();
        }
      } else if (exampleList.length > 0) {
        const nextProject = await projectStore.current.getProject(exampleList[0].id);
        if (nextProject && !nextProject.isDeleted) {
          switchToProject(nextProject);
        } else {
          await createNewProject();
        }
      } else {
        await createNewProject();
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
      
      // Restore UI state
      const { userProjects: userList, exampleProjects: exampleList } = await projectStore.current.getActiveProjects();
      setUserProjects(userList);
      setExampleProjects(exampleList);
      
      // Try to restore the current project
      if (userList.some(p => p.id === currentProject.id) || exampleList.some(p => p.id === currentProject.id)) {
        const restoredProject = await projectStore.current.getProject(currentProject.id);
        if (restoredProject && !restoredProject.isDeleted) {
          switchToProject(restoredProject);
        }
      }
    }
  };

  const handleDuplicateProject = async () => {
    if (!currentProject || !projectStore.current) return;

    const newProject = await projectStore.current.duplicateProject(currentProject.id);
    const { userProjects, exampleProjects } = await projectStore.current.getActiveProjects();
    setUserProjects(userProjects);
    setExampleProjects(exampleProjects);
    await switchToProject(newProject);
  };

  const handleStationUpdate = (updatedStation: Station) => {
    if (!currentProject) return;

    const newProject = {
      ...currentProject,
      network: {
        ...currentProject.network,
        stations: currentProject.network.stations.map(s =>
          s.id === updatedStation.id ? updatedStation : s
        )
      }
    };
    handleProjectChange(newProject);
  };

  const handleResetExamples = async () => {
    try {
      // Delete all example projects
      const projects = await projectStore.current?.getAllProjects();
      if (!projects) return;

      const exampleProjects = projects.filter(p => p.type === 'example');
      for (const project of exampleProjects) {
        await projectStore.current?.softDeleteProject(project.id, true);
      }

      // Reload examples
      await loadExampleProject(projectStore.current!);
      const { userProjects, exampleProjects: newExampleProjects } = await projectStore.current!.getActiveProjects();
      setUserProjects(userProjects);
      setExampleProjects(newExampleProjects);

      // Reload the page to start fresh
      window.location.reload();
    } catch (error) {
      console.error('Failed to reset examples:', error);
    }
  };

  const handleDeleteAllProjects = async () => {
    if (!projectStore.current) return;

    try {
      await projectStore.current.deleteAllProjects();
      
      // Get all projects (which will now only include example projects)
      const { userProjects, exampleProjects } = await projectStore.current.getActiveProjects();
      
      // Find the most recently edited example project
      const mostRecentExample = exampleProjects[0];
      
      if (mostRecentExample) {
        // Load and switch to the most recent example project
        const exampleProject = await projectStore.current.getProject(mostRecentExample.id);
        if (exampleProject) {
          setCurrentProject(exampleProject);
          urlManager.current.updateURL(currentView, view || undefined, 'auto', 'auto');
        }
      }
      
      // Update projects list
      setUserProjects(userProjects);
      setExampleProjects(exampleProjects);
    } catch (error) {
      console.error('Failed to delete all projects:', error);
      alert('Failed to delete all projects. Please try again.');
    }
  };

  const handleViewChange = (newView: string) => {
    if (currentProject) {
      // Clear the current subview when switching pages
      setCurrentSubView(null);
      localStorage.removeItem('railmapper_current_subview');

      // Update the URL with the new view but no subview
      urlManager.current.updateURL(currentProject.identifier, newView, null, 'user', 'view_change');
      
      // Set the new main view
      setCurrentView(newView);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center">
        <div className="text-[#4A4538]">Loading projects...</div>
      </div>
    );
  }

  if (!currentProject) {
    return (
      <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center">
        <div className="text-red-500 text-center">
          <div className="mb-4">Failed to initialize application</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#6A8CAF] text-white rounded hover:bg-[#6A8CAF]/80"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        userProjects={userProjects}
        exampleProjects={exampleProjects}
        currentProject={currentProject}
        currentView={currentView}
        onProjectSelect={handleProjectSelect}
        onViewChange={handleViewChange}
      />

      {/* Main Content */}
      <main className="flex-1 relative">
        {currentView === 'network' && currentProject && (
          <div className="absolute inset-0">
            <NetworkEditor
              project={currentProject}
              onProjectChange={handleProjectChange}
              onCytoscapeInstance={cy => setCyInstance(cy)}
              isExportMode={false}
              initialSubView={currentSubView}
            />
          </div>
        )}

        {currentView === 'config' && currentProject && (
          <ConfigForm
            project={currentProject}
            onProjectChange={handleProjectChange}
            onDeleteProject={handleDeleteProject}
            onDuplicateProject={handleDuplicateProject}
            projectStore={projectStore.current!}
            onDeleteAllProjects={handleDeleteAllProjects}
          />
        )}

        {currentView === 'results' && matrices && (
          <div className="px-3 pt-3 sm:pt-6 md:pt-8 pb-3">
            <div className="mb-4 flex items-center gap-4">
              <MatrixSelector
                manager={matrixManager.current}
                onSelectionChange={setSelectedMatrix}
              />
              <button
                onClick={() => downloadFnRef.current()}
                className="px-3 p-2 text-sm font-medium leading-5 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hidden xs:block"
              >
                Download CSV
              </button>
            </div>
            <MatrixTable
              data={selectedMatrix === 'cities' ? matrices.cities : matrices[selectedMatrix]}
              stations={currentProject.network.stations}
              type={selectedMatrix}
              project={currentProject}
              onStationUpdate={handleStationUpdate}
              onDownload={(fn) => { downloadFnRef.current = fn; }}
              initialView={currentSubView}
            />
          </div>
        )}

        {currentView === 'summary' && matrices && currentProject && (
          <SummaryView
            project={currentProject}
            matrices={matrices}
          />
        )}

        {currentView === 'export' && matrices && (
          <div className="relative h-full">
            <NetworkEditor
              project={currentProject}
              onProjectChange={handleProjectChange}
              onCytoscapeInstance={cy => {
                setCyInstance(cy);
              }}
              isExportMode={true}
              className="absolute inset-0"
            />
            <div className="absolute inset-0 z-10 pointer-events-auto">
              <ExportView
                project={currentProject}
                matrices={matrices}
                cy={cyInstance}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/" element={<Navigate to="/project" replace />} />
        <Route path="/project" element={<ProjectView />} />
        <Route path="/project/:identifier" element={<ProjectView />} />
        <Route path="/project/:identifier/:page" element={<ProjectView />} />
        <Route path="/project/:identifier/:page/:view" element={<ProjectView />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App; 